import clsx from "clsx";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetContactQuery } from "../../../../../features/contactsApi";
import { useFindOnePersonQuery } from "../../../../../features/peopleApi";
import { useFindAllRecallsQuery } from "../../../../../features/recallsApi";
import { ContactType } from "../../../../lib/filters/dto";
import useCreateBorder from "../../../../lib/hooks/createBorder";
import { useTabs } from "../../../../lib/tabValue";
import { DatabaseSection, TargetSection } from "../../../../lib/utils";
import { colors } from "../../../../ui/colors";
import { PlusIcon } from "../../../../ui/icons/plus";
import { RotateIcon } from "../../../../ui/icons/rotate";
import Avatar from "../../../../ui/molecules/avatar";
import { Spinner } from "../../../../ui/molecules/spinner";
import { TabMenu } from "../../../../ui/organisms/tabs/tabMenu";
import { TabPages } from "../../../../ui/organisms/tabs/tabPages";
import { ProjectDTO } from "../../../projects/dto";
import { EmailsMessagesDTO, FindAllCustomMailsResponseDTO } from "../../../projects/mails/dto";
import { SendEmailModal } from "../../../projects/mails/sendEmailModal";
import { SentMailsList } from "../../../projects/mails/sentMailsList";
import { ContactForm } from "../../../targetList/contacts/edit/form";
import { EditContactNotes } from "../../../targetList/contacts/edit/notes";
import { Recall } from "../../../targetList/contacts/recall";
import { ContactStateEnum, TargetListDTO } from "../../../targetList/dto";
import { PersonCompany } from "./company";
import { EditPersonForm } from "./form";

export enum PeopleTabValue {
  form = 'form',
  company = 'company',
  notes = 'notes',
  emails = 'emails',
  recall = 'recall'
}

interface Props {
  section: DatabaseSection | TargetSection
  listIndex: number
  targetList?: TargetListDTO
  project?: ProjectDTO
  emailsHistory?: { messages: EmailsMessagesDTO[] }
  templates?: FindAllCustomMailsResponseDTO
}

export function EditPerson(props: Props) {
  //REFACTOR
  const { contactId, detailTab, targetContactId, projectId } = useParams()
  const navigate = useNavigate()
  const { section, targetList, project, emailsHistory, templates } = props
  const { data: personResponse, isLoading } = useFindOnePersonQuery(contactId!, {
    skip: contactId === undefined
  })
  const { data: contactResponse, isLoading: isLoadingContact } = useGetContactQuery({ contactId: targetContactId! }, {
    skip: targetContactId === undefined
  })
  const { data: recallsData, isLoading: isLoadingRecalls } = useFindAllRecallsQuery({
    page: 0,
    pageSize: 0,
    sort: "",
    sortBy: "",
    fromDateTime: null,
    toDateTime: null,
    contactId: targetContactId!
  }, {
    skip: targetContactId === undefined
  })


  const contact = contactResponse
  const person = personResponse?.person
  const associateCompany = personResponse?.associatedCompany

  const prevSectionPath = new Map<typeof section, string>([
    ["target", "/edit-target-list/" + targetList?.id + "/people/" + projectId],
    ["database", "/database/people"],
    ["verify-list", "/verify-bins/people"],
    ['black-list', '/black-list/people'],
    ['target-bin', '/target-list-bins/' + targetList?.id + '/people/' + projectId]
  ])

  const handleClose = () => {
    navigate(prevSectionPath.get(section)!);
  }

  useEffect(() => {
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    };
    document.addEventListener('keydown', handleEscKeyPress);

    return () => {
      document.removeEventListener('keydown', handleEscKeyPress);
    };
  }, []);

  const formSection = {
    key: PeopleTabValue.form,
    icon: <></>,
    label: "Anagrafica",
    page: <EditPersonForm isInTargetList={section === "target"} person={person} key={PeopleTabValue.form} handleClose={handleClose} />
  }

  const companySection = {
    key: PeopleTabValue.company,
    icon: <></>,
    label: "Azienda",
    page: <PersonCompany isInTargetList={section === "target"} associateCompany={associateCompany} key={PeopleTabValue.company} handleClose={handleClose} person={person} />
  }

  const recallSection = {
    key: PeopleTabValue.recall,
    icon: <RotateIcon color={""} size={0} />,
    label: "Richiami",
    page: <Recall recalls={recallsData!} project={project!} targetId={targetList?.id!} contactId={contact?.id!} title={`${person?.name ?? ''} ${person?.surname ?? ''}`} contactType={ContactType.PERSON} key={PeopleTabValue.recall} />,
  }

  const notesSection = {
    key: PeopleTabValue.notes,
    icon: <></>,
    label: "Note",
    page: <EditContactNotes contact={contact!} key={PeopleTabValue.notes} />
  }

  const emailsSection = {
    key: PeopleTabValue.emails,
    icon: <></>,
    label: "Cronologia email",
    page: <SentMailsList templates={templates!} emailsHistory={emailsHistory?.messages.filter(message => person?.emails?.includes(message.to_email))!} targetList={targetList!} key={PeopleTabValue.emails} />
    //    hide: section !== "targetlist" || (peopleState.findPersonResponse && (peopleState.findPersonResponse.emails === null || peopleState.findPersonResponse.emails?.length === 0))
  }

  const tabMaps = new Map<typeof section, { icon: JSX.Element, label: string, page: JSX.Element, key: PeopleTabValue }[]>([
    ["target", [formSection, companySection, recallSection, notesSection, emailsSection]],
    ["database", [formSection, companySection]],
    ["verify-list", [formSection, companySection]],
    ["black-list", [formSection, companySection]],
    ['target-bin', [formSection, companySection, recallSection, notesSection, emailsSection]]
  ])

  const tabs = tabMaps.get(section)!

  const { activeIndex, setActiveIndex } = useTabs(
    tabs,
    (detailTab as PeopleTabValue) ?? PeopleTabValue.form,
    (tab) => {
      const basePath = prevSectionPath.get(section)!
      return `${basePath}/${contactId}/${tab}/${targetContactId ? targetContactId : ''}`.replaceAll('//', '/')
    }
  )


  //------------------------------------------------------------------
  const [direction, setDirection] = useState<"left" | "right">("left");

  const borderStyle = useCreateBorder(section === "target", contact?.outcome ?? ContactStateEnum.DRAFT);

  return (
    <div
      className={clsx("fixed w-full bg-opacity-30 bg-neutral-950 backdrop-blur-sm right-0 bottom-0 z-50 flex-shrink-0 h-full flex items-center justify-end", {
        hidden: contactId === undefined,
      })}
      onClick={() => handleClose()}
    >
      <div
        className="bg-white w-[480px] shadow-md h-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full overflow-auto relative">
          <div
            className={clsx(
              "absolute inset-0 z-10 bg-slate-500/20 h-full w-full flex items-center justify-center",
              {
                hidden: !(isLoading || isLoadingContact || isLoadingRecalls),
              })}
          >
            <Spinner />
          </div>
          <div className={borderStyle}>
            <div className="px-6 pt-4 flex items-center justify-between">
              <Avatar
                type="info"
                imageUrl=""
                firstRow={
                  (person?.name ?? "-") +
                  " " +
                  (person?.surname ?? "-")
                }
                secondRow={person?.role ?? "Nessun ruolo assegnato al contatto"}
                size="md"
                altTextInitials={
                  ((person?.name && person?.name[0] && person?.name[0]?.toUpperCase()) ??
                    "") +
                  ((person?.surname && person?.surname[0] && person?.surname[0]?.toUpperCase()) ??
                    "")
                }
              />
              <div className="flex items-start gap-4">
                <div
                  className="cursor-pointer rotate-45"
                  onClick={() => handleClose()}
                >
                  <PlusIcon color={colors.neutral[500]} size={20} />
                </div>
              </div>
            </div>
            {
              section === "target" && <ContactForm emailsHistory={emailsHistory!} project={project!} contact={contact!} title={`${person?.name ?? ''} ${person?.surname ?? ''}`} listIndex={props.listIndex} contactData={person} contactType={ContactType.PERSON} />
            }
            {
              section === "verify-list" && person?.toVerifyOutcomes &&
              <div className="p-6">
                <div className="p-4 w-full rounded-lg bg-red-200 flex flex-col gap-2">
                  <span className="text-text-sm text-neutral-700 font-semibold">Esiti da verificare: </span>
                  <ul className="list-inside" style={{ listStyleType: "'-   ' " }}>
                    {person.toVerifyOutcomes.map((outcome, index) => (
                      <li key={index} className="text-text-sm text-neutral-700 font-medium">{outcome}</li>
                    ))}
                  </ul>
                </div>
              </div>
            }
            <TabMenu
              direction={direction}
              setDirection={(value) => setDirection(value)}
              setValue={setActiveIndex}
              value={activeIndex}
              items={tabMaps.get(section)!.map((tab, index) => ({
                icon: tab.icon,
                label: tab.label,
                index: index
              }))}
            />
            <div>
              <TabPages
                direction={direction}
                setDirection={(value) => setDirection(value)}
                pages={tabMaps.get(section)!.map((tab) => tab.page)}
                setValue={setActiveIndex}
                value={activeIndex}
              />
            </div>
          </div>
          <SendEmailModal contact={contactResponse!} contactType={ContactType.PERSON} person={person!} targetList={targetList!} listIndex={props.listIndex} name={person?.name!} project={project!} contactId={targetContactId!} />
        </div>
      </div>
    </div>
  );
}
